import { motion } from "framer-motion";
import { Play, ArrowUpRight, Check } from "lucide-react";

const trustPoints = [
    "Professionele uitstraling",
    "Klaar voor social media en websites",
    "Levering binnen 1–3 werkdagen",
];

export const Hero = ({ onPrimary, onSecondary }) => {
    return (
        <section
            id="hero"
            data-testid="hero-section"
            className="relative pt-36 pb-12 md:pb-16 overflow-hidden bg-white"
        >
            <div
                className="absolute inset-0 opacity-[0.025] pointer-events-none"
                style={{
                    backgroundImage:
                        "linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)",
                    backgroundSize: "64px 64px",
                }}
            />
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-neutral-100 border border-black/5 text-[11px] tracking-[0.2em] uppercase text-neutral-700 mb-10 font-medium"
                >
                    <span className="w-1.5 h-1.5 rounded-full bg-black animate-pulse" />
                    Premium Vastgoed Videoproductie
                </motion.div>

                <motion.h1
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.1 }}
                    className="font-heading text-[38px] sm:text-5xl md:text-7xl lg:text-[84px] font-bold tracking-tight sm:tracking-tighter leading-[1.05] md:leading-[1] text-black max-w-5xl mx-auto"
                >
                    Van vastgoedfoto's naar{" "}
                    <span className="relative inline-block">
                        <span className="italic font-light text-neutral-500">
                            professionele
                        </span>{" "}
                        video's.
                        <svg
                            className="absolute -bottom-2 left-0 w-full"
                            viewBox="0 0 300 12"
                            fill="none"
                            preserveAspectRatio="none"
                        >
                            <path
                                d="M2 9C50 3 150 3 298 9"
                                stroke="black"
                                strokeWidth="3"
                                strokeLinecap="round"
                            />
                        </svg>
                    </span>
                </motion.h1>

                <motion.p
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.25 }}
                    className="mt-8 text-base md:text-xl text-neutral-600 max-w-2xl mx-auto leading-relaxed px-2"
                >
                    Wij transformeren bestaande vastgoedfoto's naar
                    cinematische video's voor een sterkere presentatie op
                    social media, websites en vastgoedplatformen.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                    className="mt-10 flex flex-row flex-wrap items-center justify-center gap-3"
                >
                    <button
                        data-testid="hero-primary-cta"
                        onClick={onPrimary}
                        className="btn-shine rounded-full bg-black text-white font-semibold text-base md:text-base px-7 md:px-8 py-3.5 md:py-4 transition-all duration-300 hover:bg-neutral-900 hover:scale-[1.03] hover:shadow-[0_20px_40px_-10px_rgba(0,0,0,0.35)] inline-flex items-center justify-center gap-2"
                    >
                        <span>Plan mijn gesprek</span>
                        <ArrowUpRight className="w-4 h-4" />
                    </button>
                    <button
                        data-testid="hero-secondary-cta"
                        onClick={onSecondary}
                        className="rounded-full bg-white border border-black/10 text-black font-medium text-base md:text-base px-7 md:px-8 py-3.5 md:py-4 transition-all duration-300 hover:bg-neutral-50 hover:border-black/30 hover:scale-[1.03] inline-flex items-center justify-center gap-2 group"
                    >
                        <Play className="w-4 h-4 transition-transform duration-300 group-hover:scale-110" />
                        Bekijk een voorbeeld
                    </button>
                </motion.div>

                {/* Trust list (direct onder CTA's) */}
                <motion.ul
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.55 }}
                    className="mt-8 flex flex-row flex-wrap items-center justify-center gap-x-4 gap-y-2 sm:gap-6 text-xs sm:text-sm text-neutral-700"
                >
                    {trustPoints.map((p) => (
                        <li
                            key={p}
                            className="inline-flex items-center gap-1.5 sm:gap-2"
                        >
                            <span className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-black text-white flex items-center justify-center flex-shrink-0">
                                <Check
                                    className="w-2.5 h-2.5 sm:w-3 sm:h-3"
                                    strokeWidth={3}
                                />
                            </span>
                            <span>{p}</span>
                        </li>
                    ))}
                </motion.ul>

                {/* Video Showcase */}
                <motion.div
                    initial={{ opacity: 0, y: 60 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.5 }}
                    className="mt-12 md:mt-16 relative max-w-4xl mx-auto group"
                >
                    <div className="absolute -inset-6 bg-gradient-to-br from-neutral-200 to-neutral-100 rounded-[2rem] blur-2xl opacity-60 group-hover:opacity-90 transition-opacity duration-700" />
                    <div className="relative rounded-[28px] overflow-hidden border border-black/10 shadow-[0_40px_100px_-25px_rgba(0,0,0,0.3)] bg-black">
                        <div className="relative w-full aspect-video">
                            <iframe
                                data-testid="hero-showcase-video"
                                src="https://player.vimeo.com/video/1187803627?autoplay=1&muted=1&loop=1&title=0&byline=0&portrait=0"
                                title="Marlea Showreel"
                                allow="autoplay; fullscreen; picture-in-picture"
                                allowFullScreen
                                className="absolute inset-0 w-full h-full border-0"
                            />
                        </div>
                        <div className="absolute top-4 left-4 md:top-5 md:left-5 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur-md text-[10px] tracking-[0.2em] uppercase text-black font-semibold z-10 pointer-events-none">
                            Marlea Showreel
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

